import { ReadMore as ReadMoreType } from '@/schemas/sceneDataSchema';
import { Collapse, Icon, IconButton, styled } from '@mui/material';
import { ReactNode, useState } from 'react';
import useAnalytics from '@/hooks/use-analytics';

interface ReadMoreProps {
  component?: ReadMoreType;
  children: ReactNode;
}

interface StyledButtonProps {
  isOpen: boolean;
}

interface StyledCollapseProps {
  isClamped?: boolean;
}

export const ReadMore: React.FC<ReadMoreProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const { trackEvent } = useAnalytics();
  const handleToggle = () => {
    if (isOpen) {
      trackEvent('READ_MORE_COLLAPSED');
    } else {
      trackEvent('READ_MORE_EXPANDED');
    }
    setIsOpen((prev) => !prev);
  };

  const handleIsClamped = () => {
    setIsClamped((prev) => !prev);
  };

  // 1.125rem font size * 1.5 line height (based on Body1) * 3 lines
  const collapsedSize = `${1.125 * 1.5 * 3}rem`;
  return (
    <>
      <StyledCollapse
        in={isOpen}
        isClamped={isClamped}
        collapsedSize={collapsedSize}
        onExited={handleIsClamped}
        onEnter={handleIsClamped}
        timeout='auto'
      >
        {children}
        <StyledReadMoreButton isOpen={isOpen}>
          <StyledIconButton
            onClick={handleToggle}
            aria-expanded={isOpen}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            isOpen={isOpen}
          >
            <Icon>expand_more</Icon>
          </StyledIconButton>
        </StyledReadMoreButton>
      </StyledCollapse>
    </>
  );
};

const StyledReadMoreButton = styled('div')<StyledButtonProps>`
  position: absolute;
  bottom: 0;
  right: 0;
  height: ${1.125 * 1.5}rem;
  width: 25%;
  background: ${(props) => (props.isOpen ? 'transparent' : 'rgba(255, 255, 255, 0.5)')};
`;

const StyledIconButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== 'isOpen' })<StyledButtonProps>`
  position: absolute;
  justify-content: end;
  right: 0;
  color: ${(props) => props.theme.palette.primary.main};
  border-radius: 0;
  padding: 0;
  width: 100%;
  & .material-icons {
    transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    transition: transform 0.2s;
  }
`;

const StyledCollapse = styled(Collapse, { shouldForwardProp: (prop) => prop !== 'isClamped' })<StyledCollapseProps>`
  position: relative;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${(props) => (props.isClamped ? 0 : 3)};
  overflow: hidden;
  text-overflow: ellipsis;
`;
